import rehypeRaw from 'rehype-raw';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { escapeStringRegexp } from './escapeStringRegexp';
export interface TokenValue {
  TokenName: string | undefined;
  Replacement: string | undefined;
  TokenTypeId: number | undefined;
}

enum TokenType {
  HTML = 1,
  Markdown = 2,
  Text = 3,
  JSON = 4,
  Camilyo = 5,
}

/**
 * Regexp expression that matches all tokens
 */
export const ALL_TOKEN_REGEX = BuildTokenRegex('.+?', false);

export const MAX_TOKEN_RESOLVE_DEPTH = 2;
/**
 * Build a Regexp expression that will match this token
 * @param token the token to match
 * @param escapestring whether or not to escape the regex characters in tokenName
 * @returns a regexp that will match the token
 */
function BuildTokenRegex(tokenName?: string, escapeString = true): RegExp {
  tokenName = (
    escapeString ? escapeStringRegexp(tokenName ?? '') : (tokenName ?? '')
  ).trim();
  return new RegExp(
    `(?:\\[\\[\\s*(${tokenName})\\s*\\]\\])|(?:@@\\s*(${tokenName})\\s*@@)`,
    'gi'
  );
}

/**
 * Detect whether a string contains at least one HTML node
 * @param str the string to parse & check
 * @returns true if a >= 1 html node detected, false otherwise
 */
function isHTML(str: string): boolean {
  const htmlRegex = /(<([^>]+)>)/i;
  return htmlRegex.test(str);
}

/**
 * Replace all instances of tokens within in a string
 * @param content the string to replace tokens in
 * @param tokens the tokens to replace in the content
 * @param convertToHTMLLineBreaks whether or not new lines are converted to <br> tags
 * @param recursiveDepth how many recursive levels to resolve tokens (e.g. a token contains another token)
 * @returns content with all instances of the passed tokens replaced with their replacements
 */
export function TokenReplace(
  content: string,
  token:
    | TokenValue
    | TokenValue[]
    | Record<string, { value: string; typeId: number }>,
  convertToHTMLLineBreaks = false,
  recursiveDepth = MAX_TOKEN_RESOLVE_DEPTH
): string {
  if (content == null) {
    return content;
  }
  if (typeof content !== 'string') {
    return String(content);
  }
  let tokenMap: Record<string, { value: string; typeId: number }> = {};

  // Compute the token map for faster token lookup
  if ('TokenName' in token || Array.isArray(token)) {
    tokenMap = [token as TokenValue | TokenValue[]].flat().reduce(
      (acc, t) => {
        if (t.TokenName != null) {
          acc[t.TokenName!.toLowerCase()] = {
            value: t.Replacement!,
            typeId: t.TokenTypeId!,
          };
        }
        return acc;
      },
      {} as typeof tokenMap
    );
  }
  // Don't recompute the token map on recursive calls
  else {
    tokenMap = token as unknown as Record<
      string,
      { value: string; typeId: number }
    >;
  }

  if (Object.keys(tokenMap).length === 0) return content;

  let modified = false;
  const contentWithTokensReplaced = content.replace(
    ALL_TOKEN_REGEX,
    (matchedStr: string, group1: string, group2: string) => {
      const tokenName = (group1 ?? group2).toLowerCase();
      const tokenReplacementObj = tokenMap?.[tokenName];
      if (tokenReplacementObj == null) return matchedStr;
      const tokenReplacement = transformTokenValue(
        tokenReplacementObj.typeId,
        tokenReplacementObj.value,
        convertToHTMLLineBreaks
      );

      modified = true;
      return tokenReplacement;
    }
  );

  if (recursiveDepth > 0 && modified) {
    return TokenReplace(
      contentWithTokensReplaced,
      tokenMap,
      convertToHTMLLineBreaks,
      recursiveDepth - 1
    );
  }
  return contentWithTokensReplaced;
}

/**
 * Removes all tokens from a string
 * @param content the string to remove tokens from
 * @returns content with all of its tokens removed
 */
export function RemoveUnresolvedTokens(content: string): string {
  return content.replace(ALL_TOKEN_REGEX, '');
}

/**
 * Find all tokens within our content list and return an array of their TokenNames
 * @returns an array of TokenNames used in the content
 */
export function FindTokenNamesInContent(...contentList: string[]): string[] {
  return Array.from(
    new Set(
      contentList.flatMap((content) =>
        Array.from(content.matchAll(ALL_TOKEN_REGEX)).map((m) => m[1] ?? m[2])
      )
    )
  );
}

function transformTokenValue(
  tokenTypeId: number,
  value: string,
  convertToHTMLLineBreaks: boolean
): string {
  if (typeof value !== 'string') return value;

  let result = value;
  switch (tokenTypeId) {
    case TokenType.HTML: {
      result = value;
      break;
    }
    case TokenType.Markdown: {
      const markdownWithHtmlContent = parseMarkdownWithHtml(value);
      result = markdownWithHtmlContent;
      break;
    }
    case TokenType.Text: {
      result = stripHTML(value);
      break;
    }
    case TokenType.JSON: {
      try {
        const parsedJSON = JSON.parse(value);
        result = JSON.stringify(parsedJSON, null, 2);
      } catch {
        result = 'Invalid JSON';
      }
      break;
    }
    default: {
      if (convertToHTMLLineBreaks && !isHTML(value)) {
        result = result.replace(/\n/g, '<br/>');
      }
      break;
    }
  }

  return result;
}

function stripHTML(tokenValue: string): string {
  return tokenValue.replace(/<\/?[^>]+(>|$)/g, '');
}

const parseMarkdownWithHtml = (markdown: string): string => {
  const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeStringify);

  const result = processor.processSync(markdown);
  return result.toString();
};
